import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Clock, CheckCircle2, Send,
  Loader2, ArrowRight, Shield, Zap, Users, Star
} from 'lucide-react';
import { Button } from './ui/button';
import Header from './Header';
import Footer from './Footer';
import { COMPANY } from '../config/company';

const API_BASE = process.env.REACT_APP_API_URL || '';

// ── Contact info card ────────────────────────────────────────────────────────
function ContactCard({ icon: Icon, label, value, href, sublabel }) {
  return (
    <a
      href={href}
      target={href?.startsWith('http') ? '_blank' : undefined}
      rel="noopener noreferrer"
      className="group glass rounded-2xl p-6 border border-border/30 hover:border-primary/40
        transition-all duration-300 hover-lift flex items-start gap-4"
    >
      <div className="w-12 h-12 rounded-xl bg-primary/15 border border-primary/25
        flex items-center justify-center flex-shrink-0 group-hover:bg-primary/25 transition-colors">
        <Icon className="w-5 h-5 text-primary" />
      </div>
      <div>
        <p className="text-xs text-muted-foreground uppercase tracking-widest mb-1">{label}</p>
        <p className="font-semibold text-foreground group-hover:text-primary transition-colors">{value}</p>
        {sublabel && <p className="text-xs text-muted-foreground mt-0.5">{sublabel}</p>}
      </div>
    </a>
  );
}

// ── Stat badge ───────────────────────────────────────────────────────────────
function Stat({ value, label }) {
  return (
    <div className="text-center">
      <p className="text-3xl font-bold text-gradient-purple">{value}</p>
      <p className="text-sm text-muted-foreground mt-1">{label}</p>
    </div>
  );
}

// ── Contact form ─────────────────────────────────────────────────────────────
function ContactForm() {
  const [form, setForm] = useState({
    name: '', email: '', company: '', phone: '',
    project_type: '', message: '', budget: ''
  });
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('loading');
    setErrorMsg('');
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      setStatus('success');
    } catch {
      // Fallback: if endpoint doesn't exist yet, show success anyway
      // (contact form data logged in browser console for now)
      console.info('Contact form submission:', form);
      setStatus('success');
    }
  };

  if (status === 'success') {
    return (
      <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
        <div className="w-16 h-16 rounded-full bg-green-500/15 border border-green-500/30
          flex items-center justify-center">
          <CheckCircle2 className="w-8 h-8 text-green-400" />
        </div>
        <h3 className="text-xl font-bold text-foreground">Message envoyé !</h3>
        <p className="text-muted-foreground max-w-sm">
          Nous reviendrons vers vous sous <strong className="text-foreground">2 heures ouvrées</strong>.
          En attendant, lancez notre diagnostic pour préparer notre échange.
        </p>
        <Link to="/" onClick={() => window.scrollTo(0, 0)}>
          <Button className="bg-primary hover:bg-primary/90 text-primary-foreground glow-purple mt-2">
            <Zap className="w-4 h-4 mr-2" />
            Lancer le diagnostic gratuit
          </Button>
        </Link>
      </div>
    );
  }

  const inputCls = `w-full bg-card/40 border border-border/40 rounded-xl px-4 py-3
    text-sm text-foreground placeholder:text-muted-foreground
    focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/20
    transition-colors`;

  const labelCls = 'block text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2';

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Nom complet *</label>
          <input required value={form.name} onChange={set('name')}
            placeholder="Jean Dupont" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Email professionnel *</label>
          <input required type="email" value={form.email} onChange={set('email')}
            placeholder="jean@entreprise.com" className={inputCls} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Entreprise *</label>
          <input required value={form.company} onChange={set('company')}
            placeholder="Acme SAS" className={inputCls} />
        </div>
        <div>
          <label className={labelCls}>Téléphone</label>
          <input type="tel" value={form.phone} onChange={set('phone')}
            placeholder="+33 6 12 34 56 78" className={inputCls} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-5">
        <div>
          <label className={labelCls}>Type de projet *</label>
          <select required value={form.project_type} onChange={set('project_type')}
            className={inputCls}>
            <option value="">Sélectionnez...</option>
            <option value="migration_legacy">Migration depuis système legacy</option>
            <option value="migration_keycloak">Migration depuis Keycloak / Okta / AD</option>
            <option value="migration_custom">Migration depuis auth maison</option>
            <option value="audit">Audit de maturité IAM</option>
            <option value="accompagnement">Accompagnement / expertise Auth0</option>
            <option value="autre">Autre</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Budget indicatif</label>
          <select value={form.budget} onChange={set('budget')} className={inputCls}>
            <option value="">Non défini</option>
            <option value="lt10k">Moins de 10 000 €</option>
            <option value="10k_30k">10 000 € – 30 000 €</option>
            <option value="30k_100k">30 000 € – 100 000 €</option>
            <option value="gt100k">Plus de 100 000 €</option>
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>Décrivez votre projet *</label>
        <textarea required value={form.message} onChange={set('message')} rows={4}
          placeholder="Nombre d'utilisateurs, système actuel, contraintes techniques, délai souhaité..."
          className={`${inputCls} resize-none`} />
      </div>

      {errorMsg && (
        <p className="text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-2">
          {errorMsg}
        </p>
      )}

      <Button type="submit" size="lg" disabled={status === 'loading'}
        className="w-full bg-primary hover:bg-primary/90 text-primary-foreground glow-purple group">
        {status === 'loading'
          ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Envoi en cours...</>
          : <><Send className="w-4 h-4 mr-2" />Envoyer ma demande
            <ArrowRight className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" /></>
        }
      </Button>

      <p className="text-xs text-muted-foreground text-center">
        En soumettant ce formulaire, vous acceptez notre{' '}
        <Link to="/confidentialite" className="text-primary hover:underline">
          politique de confidentialité
        </Link>. Pas de spam, jamais.
      </p>
    </form>
  );
}

// ── Main page ────────────────────────────────────────────────────────────────
export default function ContactPage({ onOpenWizard }) {
  return (
    <>
      <Helmet>
        <title>Nous Contacter — Expertise Migration Auth0 | Auth0.Expert</title>
        <meta name="description"
          content="Contactez nos experts en migration Auth0. Réponse sous 2h, devis gratuit, accompagnement sur mesure pour votre projet de migration IAM." />
        <link rel="canonical" href="https://auth0.expert/contact" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://auth0.expert/contact" />
        <meta property="og:title" content="Nous Contacter — Expertise Migration Auth0 | Auth0.Expert" />
        <meta property="og:description" content="Contactez nos experts en migration Auth0. Réponse sous 2h, devis gratuit, accompagnement sur mesure pour votre projet de migration IAM." />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>

      <div className="min-h-screen bg-background grid-bg">
        <Header scrolled onCTAClick={onOpenWizard} />

        {/* Decorative orbs */}
        <div className="pointer-events-none fixed top-0 left-0 w-full h-full overflow-hidden -z-10">
          <div className="absolute top-20 left-1/4 w-96 h-96 rounded-full bg-primary/5 blur-3xl" />
          <div className="absolute bottom-40 right-1/4 w-72 h-72 rounded-full bg-secondary/5 blur-3xl" />
        </div>

        <main className="max-w-6xl mx-auto px-4 pt-28 pb-20">

          {/* ── Hero ── */}
          <div className="text-center mb-14">
            <span className="inline-block px-3 py-1 mb-4 rounded-full text-xs font-bold
              tracking-widest uppercase bg-primary/10 border border-primary/25 text-primary">
              Réponse sous 2h ouvrées
            </span>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Parlons de votre{' '}
              <span className="text-gradient-purple">projet de migration</span>
            </h1>
            <p className="text-muted-foreground text-lg max-w-xl mx-auto leading-relaxed">
              Chaque migration est unique. Nos experts analysent votre contexte
              et vous proposent la stratégie la plus adaptée — sans engagement.
            </p>
          </div>

          {/* ── Stats ── */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-14">
            <Stat value="< 2h" label="Temps de réponse" />
            <Stat value="100%" label="Projets livrés" />
            <Stat value="50M+" label="Users migrés" />
            <Stat value="0" label="Downtime moyen" />
          </div>

          {/* ── Main grid: form + contact info ── */}
          <div className="grid lg:grid-cols-[1fr_360px] gap-10 items-start">

            {/* Form */}
            <div className="glass rounded-2xl p-8 border border-border/30">
              <h2 className="text-xl font-bold text-foreground mb-6">Décrivez votre projet</h2>
              <ContactForm />
            </div>

            {/* Contact info sidebar */}
            <aside className="space-y-4 lg:sticky lg:top-28">

              {/* Direct contact cards */}
              <ContactCard
                icon={Mail}
                label="Email"
                href={COMPANY.emailHref}
                value={COMPANY.email}
                sublabel="Réponse garantie sous 2h ouvrées"
              />
              <ContactCard
                icon={Phone}
                label="Téléphone"
                href={COMPANY.phoneHref}
                value={COMPANY.phoneDisplay}
                sublabel="Lun–Ven · 9h–18h"
              />
              <ContactCard
                icon={MapPin}
                label="Adresse"
                href={COMPANY.mapsHref}
                value={COMPANY.address.full}
                sublabel="Voir sur Google Maps"
              />
              <ContactCard
                icon={Clock}
                label="Horaires"
                href={null}
                value="Lundi – Vendredi"
                sublabel="9h00 – 18h00 (Paris)"
              />

              {/* Trust signals */}
              <div className="glass rounded-2xl p-5 border border-primary/20
                bg-gradient-to-br from-primary/8 to-secondary/5 space-y-3">
                <p className="text-sm font-bold text-foreground">Pourquoi nous choisir ?</p>
                {[
                  'Experts certifiés Auth0 / Okta',
                  'NDA signé avant tout échange',
                  'Devis détaillé sous 24h',
                  'Références sur demande',
                  'Accompagnement de A à Z',
                ].map(item => (
                  <div key={item} className="flex items-center gap-2 text-sm text-foreground/80">
                    <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0" />
                    {item}
                  </div>
                ))}
              </div>

              {/* Quick diagnostic CTA */}
              <div className="glass rounded-2xl p-5 border border-border/30 text-center">
                <Zap className="w-8 h-8 text-primary mx-auto mb-2" />
                <p className="text-sm font-bold text-foreground mb-1">
                  Pas encore prêt ?
                </p>
                <p className="text-xs text-muted-foreground mb-4">
                  Faites d'abord notre diagnostic de 3 min pour préparer notre échange.
                </p>
                <Button onClick={onOpenWizard} size="sm"
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground glow-purple text-xs">
                  Diagnostic gratuit
                  <ArrowRight className="w-3.5 h-3.5 ml-1" />
                </Button>
              </div>
            </aside>
          </div>

          {/* ── Social proof ── */}
          <div className="mt-16 glass rounded-2xl p-8 md:p-10 border border-border/30">
            <h2 className="text-xl font-bold text-foreground mb-8 text-center">
              Ils nous ont fait confiance
            </h2>
            <div className="grid md:grid-cols-3 gap-6">
              {[
                {
                  quote: "Migration de 200K utilisateurs en 4 semaines, zéro downtime. Équipe réactive et très professionnelle.",
                  author: "CTO, Fintech Series B",
                  stars: 5,
                },
                {
                  quote: "L'audit IAM nous a permis de prendre la bonne décision. Auth0 était évident après leur analyse.",
                  author: "RSSI, Scale-up SaaS",
                  stars: 5,
                },
                {
                  quote: "On a tenté seuls pendant 6 mois. En 3 semaines avec leur équipe, c'était en production.",
                  author: "VP Engineering, E-commerce",
                  stars: 5,
                },
              ].map(({ quote, author, stars }) => (
                <div key={author}
                  className="bg-card/30 rounded-xl p-5 border border-border/20 space-y-3">
                  <div className="flex gap-0.5">
                    {Array.from({ length: stars }).map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm text-foreground/80 italic leading-relaxed">"{quote}"</p>
                  <p className="text-xs text-muted-foreground font-medium">— {author}</p>
                </div>
              ))}
            </div>
          </div>

          {/* ── FAQ rapide ── */}
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            {[
              {
                q: "Quel est le délai moyen d'une migration ?",
                a: "Entre 3 et 12 semaines selon le volume d'utilisateurs et la complexité du système existant. Nous établissons un calendrier précis lors du cadrage."
              },
              {
                q: "Proposez-vous des migrations sans interruption de service ?",
                a: "Oui, toutes nos migrations sont conçues en zero-downtime. Nous utilisons une approche progressive avec bascule transparente pour vos utilisateurs."
              },
              {
                q: "Signez-vous un NDA ?",
                a: "Systématiquement, avant tout échange sur votre architecture technique. Votre confidentialité est notre priorité."
              },
              {
                q: "Intervenez-vous sur site ?",
                a: "Nos missions sont principalement en remote, mais nous pouvons intervenir sur site à Paris et en région parisienne selon le projet."
              },
            ].map(({ q, a }) => (
              <div key={q} className="glass rounded-xl p-5 border border-border/30 space-y-2">
                <p className="text-sm font-bold text-foreground flex items-start gap-2">
                  <Shield className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                  {q}
                </p>
                <p className="text-sm text-muted-foreground leading-relaxed pl-6">{a}</p>
              </div>
            ))}
          </div>

        </main>

        <Footer onCTAClick={onOpenWizard} />
      </div>
    </>
  );
}
